// Feature flags. Flip a value here to re-expose a hidden feature without
// having to revert UI code. Keep this list short. Only flag features whose
// rollout is genuinely uncertain.

/**
 * Dynamic pricing rules (occupancy-based price adjustments).
 *
 * Hidden from the user-facing surfaces while we hold the feature back. When
 * false: the onboarding "Boost your revenue" step is skipped, the "Dynamic
 * Pricing" button on the Listings page disappears, and the
 * /?tab=dynamic-pricing route renders nothing.
 *
 * Customer-facing pricing computation (useDynamicPricing hook, DynamicTimeSlots,
 * the booking pages) is intentionally NOT gated. Any rules already present in
 * the database continue to apply to live bookings.
 */
export const DYNAMIC_PRICING_ENABLED = false;

/**
 * Self-serve onboarding (businesses signing themselves up).
 *
 * When false, businesses can no longer create their own account / onboard
 * themselves. Only Dibs staff can onboard a business via the internal
 * onboarding wizard (internal.* subdomain). When false:
 *   - Landing-page "Start for free" / "Try free forever" CTAs repoint to the
 *     Book-a-demo link instead of the self-serve signup at /auth?source=free_trial.
 *   - The Auth page hides the "Sign up" tab and Google-signup button, leaving
 *     login-only. Existing customers and staff invites can still sign in / set
 *     their password.
 *   - Brand-new accounts are rejected at the auth layer (incl. Google OAuth).
 *
 * Nothing is deleted: the self-serve onboarding wizard, its /onboarding route,
 * and the create-checkout edge function all remain in place. Flip this to true
 * to fully re-enable self-serve signup with no other code changes.
 *
 * The internal onboarding wizard and staff-invite signup are NOT gated by this
 * flag and keep working regardless.
 */
export const SELF_SERVE_ONBOARDING_ENABLED = true;

/**
 * Appointment (resource-based) booking. The salon/clinic vertical.
 *
 * Dibs Booking is going vertical for CLASS-BASED businesses (tuition / enrichment
 * / dance / music / swim), where the only model that matters is the fixed-slot
 * group "session". Appointment mode (book a specific staff member / room / chair
 * for a duration) exists only to serve salons & clinics. The horizontal
 * customers we're stepping away from.
 *
 * When false:
 *   - The "Session vs By appointment" booking-type selector is hidden in the
 *     listing editor and onboarding; every listing is created as 'session'.
 *   - Editing any listing re-pins booking_mode='session' (duration_minutes null,
 *     buffer_minutes 0) so a legacy appointment listing can't be re-saved as one.
 *   - The "Staff" (resources) tab is hidden from the Dibs Bookings workspace,
 *     and a stale ?tab=resources URL renders nothing.
 *   - The storefront appointment branch is unreachable for new listings; any
 *     legacy appointment listing degrades to "not bookable online" rather than a
 *     half-built staff picker.
 *
 * Nothing is deleted: the `resources` table, the booking_mode/duration_minutes/
 * buffer_minutes columns, and the appointment edge functions
 * (get-appointment-availability, book_appointment_atomic, resourceAvailability)
 * all remain in place. Flip this to true to restore the horizontal product with
 * no other code changes. A hard DB-level teardown is deferred until we're
 * certain no live business is on appointment mode.
 */
export const APPOINTMENT_BOOKING_ENABLED = false;

/**
 * Programme management. The recurring class-enrolment suite: Enrolments,
 * Attendance, and Make-ups.
 *
 * Held back from the Dibs Bookings workspace while we focus the product on the
 * core single-session booking + AI automation loop. When false:
 *   - The "Enrolments", "Attendance", and "Make-ups" tabs are hidden from the
 *     Dibs Bookings inner sidebar.
 *   - A stale ?tab=enrolments / attendance / make-ups URL redirects to the
 *     Bookings tab instead of rendering an empty pane.
 *
 * This is purely a UI hide. Nothing is deleted or disabled server-side:
 *   - The `attendance_status` column (written by AttendanceRoster) is SEPARATE
 *     from `bookings.status`. The AI no-show / trial-to-paid / post-session
 *     workflows run off `bookings.status` via the Bookings tab, so they are
 *     unaffected.
 *   - The make-up backend (make-up-claim, makeUpAutoOffer, opt-in, default OFF)
 *     and the make_up_credits table remain in place but are dormant without UI.
 *
 * Flip to true to restore the full programme-management suite with no other
 * code changes.
 */
export const PROGRAMME_MANAGEMENT_ENABLED = false;

/**
 * Google Reviews connector & reputation management.
 *
 * On: owners paste their Google review link (no Google API access needed),
 * which workflows and the chat AI share with customers. The optional "Connect
 * Google" half stays disabled in the card until GOOGLE_BUSINESS_CLIENT_ID /
 * GOOGLE_BUSINESS_CLIENT_SECRET are set on the platform. When false, the
 * "Reputation & reviews" card is hidden from Connections.
 */
export const GOOGLE_REVIEWS_CONNECTOR_ENABLED = true;
